/* ==========================================================================
   cs-core.js — shared behaviour for all cs-* sections.
   No dependencies. Safe to load with `defer`.
   ========================================================================== */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- Rail  */
  /* <cs-rail> wrapping .cs-rail__track and optional prev/next buttons.     */

  class CsRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.cs-rail__track');
      if (!this.track) return;

      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');

      this.onScroll = this.debounce(() => this.syncButtons(), 80);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      this.prev && this.prev.addEventListener('click', () => this.page(-1));
      this.next && this.next.addEventListener('click', () => this.page(1));

      // Re-measure when a hidden panel becomes visible (tabbed sections).
      this.resizeObserver = new ResizeObserver(() => this.syncButtons());
      this.resizeObserver.observe(this.track);

      this.syncButtons();
    }

    disconnectedCallback() {
      this.resizeObserver && this.resizeObserver.disconnect();
    }

    /** Scroll by one visible page of items. */
    page(direction) {
      const item = this.track.firstElementChild;
      if (!item) return;
      const styles = getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      const step = item.getBoundingClientRect().width + gap;
      const perPage = Math.max(1, Math.floor(this.track.clientWidth / step));
      this.track.scrollBy({ left: step * perPage * direction, behavior: 'smooth' });
    }

    syncButtons() {
      if (!this.prev || !this.next) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      const x = this.track.scrollLeft;
      this.prev.disabled = x <= 2;
      this.next.disabled = x >= max - 2;
    }

    debounce(fn, wait) {
      let t;
      return function () {
        clearTimeout(t);
        t = setTimeout(fn, wait);
      };
    }
  }
  customElements.define('cs-rail', CsRail);

  /* ---------------------------------------------------------------- Tabs  */
  /* <cs-tabs> containing [role=tab] buttons and [role=tabpanel] panels.    */

  class CsTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      if (!this.tabs.length) return;

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab));
        tab.addEventListener('keydown', (e) => this.onKey(e));
      });
    }

    select(tab) {
      this.tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        const panel = this.querySelector('#' + t.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
      // Nudge any rails inside the newly shown panel to re-measure.
      window.dispatchEvent(new Event('resize'));
    }

    onKey(e) {
      const i = this.tabs.indexOf(document.activeElement);
      if (i < 0) return;
      let target = null;
      if (e.key === 'ArrowRight') target = this.tabs[(i + 1) % this.tabs.length];
      if (e.key === 'ArrowLeft') target = this.tabs[(i - 1 + this.tabs.length) % this.tabs.length];
      if (e.key === 'Home') target = this.tabs[0];
      if (e.key === 'End') target = this.tabs[this.tabs.length - 1];
      if (!target) return;
      e.preventDefault();
      target.focus();
      this.select(target);
    }
  }
  customElements.define('cs-tabs', CsTabs);

  /* -------------------------------------------------------------- Reveal  */

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!reduced && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const delay = el.dataset.csRevealDelay || 0;
          setTimeout(() => el.classList.add('is-in'), Number(delay));
          io.unobserve(el);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
    );

    const observe = () =>
      document.querySelectorAll('[data-cs-reveal]:not(.is-in)').forEach((el) => io.observe(el));

    observe();
    // Theme editor re-renders sections without a page load.
    document.addEventListener('shopify:section:load', observe);
  } else {
    document.querySelectorAll('[data-cs-reveal]').forEach((el) => el.classList.add('is-in'));
  }
})();
